// src/pages/auth/Register.js
// Page d'inscription connectée à l'API - NE PAS MODIFIER

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';

const Register = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await register(formData.name, formData.email, formData.phone, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      padding: '16px',
      backgroundColor: theme.bg,
    },
    header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer' },
    logoSection: { textAlign: 'center', marginBottom: '24px' },
    logoTitle: { fontSize: '28px', fontWeight: '700', color: theme.gold },
    logoSubtitle: { fontSize: '13px', color: theme.textSecondary, marginTop: '4px' },
    form: { display: 'flex', flexDirection: 'column', gap: '14px' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '13px', fontWeight: '600', color: theme.textSecondary },
    input: {
      padding: '14px 16px',
      backgroundColor: theme.bgInput,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      color: theme.textPrimary,
      fontSize: '15px',
      outline: 'none',
    },
    errorText: { fontSize: '13px', color: '#F56565', textAlign: 'center' },
    button: {
      padding: '16px',
      backgroundColor: theme.gold,
      border: 'none',
      borderRadius: '10px',
      color: theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      marginTop: '8px',
    },
    links: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px' },
    link: { color: theme.gold, fontSize: '14px', cursor: 'pointer' },
    text: { color: theme.textSecondary, fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Inscription | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/')}>←</button>
      </div>

      <div style={styles.logoSection}>
        <div style={styles.logoTitle}>Fynho</div>
        <div style={styles.logoSubtitle}>Créez votre compte</div>
      </div>

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Nom complet</label>
          <input type="text" style={styles.input} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Jean Kamga" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input type="email" style={styles.input} value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="votre@email.com" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Téléphone</label>
          <input type="tel" style={styles.input} value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+237 6XX XXX XXX" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Mot de passe</label>
          <input type="password" style={styles.input} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="••••••••" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Confirmer le mot de passe</label>
          <input type="password" style={styles.input} value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} placeholder="••••••••" onKeyDown={(e) => e.key === 'Enter' && handleSubmit()} />
        </div>
        {error && <div style={styles.errorText}>{error}</div>}
        <button style={{ ...styles.button, opacity: loading ? 0.6 : 1 }} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Création...' : "S'inscrire"}
        </button>
      </div>

      <div style={styles.links}>
        <span style={styles.text}>Déjà un compte ?</span>
        <span style={styles.link} onClick={() => navigate('/login')}>Se connecter</span>
      </div>
    </div>
  );
};

export default Register;